/* Nová online rezervace ve zvonečku (NotificationCenter).
 *
 * PROČ TOHLE EXISTUJE
 * -------------------
 * Klient si na /book/:slug vybere termín. Poradce o tom doteď nevěděl vůbec:
 * rezervace končila v `db.bookings`, zvoneček ani diář ji nikdy neviděly, žádná
 * notifikace, žádný úkol. Poradce ten čas považoval za volný a domluvil si na
 * něj druhého klienta.
 *
 * PROČ SE NESTAVÍ NOVÝ KANÁL
 * --------------------------
 * Backend teď rezervaci zapisuje do diáře jako běžnou položku (`db.tasks`,
 * `source: "from_booking"`, viz backend/lib/rezervace_diar.py). Zvoneček čte
 * /api/crm/tasks, takže stačí přidat skupinu — žádný cron, push ani e-mail.
 *
 * CO TO NEDĚLÁ
 * ------------
 * Nic se neodesílá. Uvidí to ten, kdo si aplikaci otevře. Odesílat rezervaci
 * poradci e-mailem/SMS je samostatná práce (a vědomé rozhodnutí, ne vedlejšák).
 *
 * DVĚ ROVINY, ZÁMĚRNĚ
 * -------------------
 *  1) `nadchazejiciRezervace` — CO ukázat v panelu (budoucí rezervace v okně),
 *  2) `noveRezervace`        — CO rozsvítí ODZNAK (co přišlo od posledního
 *     otevření panelu). Denní model odznaku („dnes už jsem to viděl") by jinak
 *     odpolední rezervaci schoval až do zítřka — a to je přesně ten případ,
 *     kdy poradce stihne slíbit ten čas někomu jinému.
 */
import { dnuDo } from "@/components/notif/akceNotif";

/** Značka položky diáře z online rezervace (backend: lib/rezervace_diar.REZERVACE_SOURCE). */
export const REZERVACE_SOURCE = "from_booking";

/** Jak daleko dopředu rezervaci ve zvonečku hlásit (dní). */
export const OKNO_DNI = 30;

/** Čas vzniku položky v ms, nebo null (nečitelné → nepovažujeme za novou). */
export function vznikMs(task) {
  const raw = task?.created_at;
  if (typeof raw !== "string" || !raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/**
 * Budoucí online rezervace z diáře, seřazené od nejbližší.
 *
 * Pravidla (každé kvůli konkrétní pasti):
 *  • jen `source === REZERVACE_SOURCE` — ostatní schůzky do zvonečku nepatří,
 *    plný kalendář by ho zahltil,
 *  • hotové (`done`) se nehlásí,
 *  • termín se čte `deadline || start_iso` — STEJNÉ pořadí jako ve zbytku
 *    zvonečku, ať položka nespadne do dvou skupin s různým dnem,
 *  • `dd <= 0` se přeskočí — dnešek řeší skupina „Dnes", minulost „Po termínu",
 *  • `dd > OKNO_DNI` se přeskočí — rezervace na příští čtvrtletí není zpráva,
 *  • nečitelný termín se přeskočí (žádné „asi zítra").
 */
export function nadchazejiciRezervace(tasks, ted = new Date()) {
  if (!Array.isArray(tasks)) return [];
  const out = [];
  for (const tk of tasks) {
    if (!tk || typeof tk !== "object") continue;
    if (tk.source !== REZERVACE_SOURCE) continue;
    if (tk.done) continue;
    const iso = tk.deadline || tk.start_iso;
    const dd = dnuDo(iso, ted);
    if (dd === null || dd <= 0) continue;
    if (dd > OKNO_DNI) continue;
    out.push({
      id: tk.id,
      task: tk,
      days: dd,
      when: new Date(iso),
      text: tk.text || "",
      klient: tk.client_name || "",
      sluzba: tk.service_label || "",
      vznik: vznikMs(tk),
    });
  }
  out.sort((a, b) => a.when - b.when);
  return out;
}

/**
 * Které z nich přišly od posledního otevření panelu → rozsvítí odznak i v den,
 * kdy si poradce zvoneček už jednou přečetl.
 *
 * Bez `lastSeen` (0 / neznámé) se nic za nové nepovažuje — jinak by odznak
 * po přihlášení na novém zařízení svítil na měsíc staré rezervace.
 */
export function noveRezervace(polozky, lastSeen) {
  if (!Array.isArray(polozky)) return [];
  const od = Number(lastSeen);
  if (!Number.isFinite(od) || od <= 0) return [];
  return polozky.filter((p) => typeof p?.vznik === "number" && p.vznik > od);
}

/**
 * Rezervace, které mají ROZSVÍTIT ODZNAK — počítá se z úkolů, ne z panelu.
 *
 * PROČ ZVLÁŠŤ: `nadchazejiciRezervace` schválně vynechává dnešek (ten patří
 * skupině „Dnes"). Odznak by tím ale přišel právě o nejnaléhavější případ:
 * poradce s `min_notice_hours` pod 24 h může dostat rezervaci NA DNES —
 * a když si zvoneček dnes už jednou otevřel, denní model by mu ji schoval do
 * zítřka. Tady tedy bereme dnešek i budoucnost, jen nové od `lastSeen`.
 */
export function rezervaceProOdznak(tasks, lastSeen, ted = new Date()) {
  const od = Number(lastSeen);
  if (!Array.isArray(tasks) || !Number.isFinite(od) || od <= 0) return [];
  return tasks.filter((tk) => {
    if (!tk || typeof tk !== "object") return false;
    if (tk.source !== REZERVACE_SOURCE || tk.done) return false;
    const dd = dnuDo(tk.deadline || tk.start_iso, ted);
    if (dd === null || dd < 0 || dd > OKNO_DNI) return false;   // minulost neřešíme
    const vznik = vznikMs(tk);
    return typeof vznik === "number" && vznik > od;
  });
}

export default nadchazejiciRezervace;
